import { Button } from '@components/Button';
import { ButtonType } from '@common/types/button';
import { IconType } from '@src/IconType.ts';
import { Skeleton } from '@components/Skeleton.tsx';
import { classNames } from '@utils/classNames.ts';
import { useTranslation } from 'react-i18next';
import { Fragment, useCallback } from 'react';

export default function PaginationPanel({
  currentPage,
  numberOfAvailablePages,
  setNewPage,
  loading,
}: {
  currentPage: number;
  numberOfAvailablePages: number;
  setNewPage: (pageNumber: number) => void;
  loading: boolean;
}) {
  const { t } = useTranslation('paginationPanel');
  numberOfAvailablePages = Math.ceil(numberOfAvailablePages);

  const generatePageButton = useCallback(
    (pageNumber: number) => {
      return (
        <button
          key={`paginationButton-${pageNumber}`}
          onClick={() => setNewPage(pageNumber)}
          disabled={currentPage === pageNumber}
          className={classNames(
            'inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg',
            currentPage === pageNumber
              ? 'text-white bg-slate-900'
              : 'text-slate-900',
          )}
          data-testid={`panel-button-${pageNumber}`}
        >
          {pageNumber}
        </button>
      );
    },
    [currentPage, setNewPage],
  );

  const getOptions = useCallback(() => {
    const options = [];
    for (let i = 1; i <= Math.min(numberOfAvailablePages, 3); i++) {
      options.push(generatePageButton(i));
    }
    options.push(
      generateFiller(numberOfAvailablePages, currentPage, generatePageButton),
    );
    for (
      let i = Math.max(4, numberOfAvailablePages - 2);
      i <= numberOfAvailablePages;
      i++
    ) {
      options.push(generatePageButton(i));
    }
    return options;
  }, [numberOfAvailablePages, currentPage, generatePageButton]);

  return (
    <nav className="flex items-center justify-between gap-2 px-6 lg:px-8 py-3 w-full">
      <Button
        disabled={currentPage === 1}
        onClick={() => setNewPage(currentPage - 1)}
        IconLeading={IconType.ArrowLeft}
        label={<span className="hidden sm:inline">{t('previous')}</span>}
        buttonType={ButtonType.SECONDARY}
        data-testid="previous-button"
      />
      {loading && numberOfAvailablePages === 0 ? (
        <Skeleton containerClassName="w-4 flex-shrink-0 basis-1/3 text-sm" />
      ) : (
        <>
          <div className="hidden 2md:-mt-px 2md:flex">{getOptions()}</div>
          <div className="block 2md:-mt-px 2md:hidden text-n500DarkGrey text-sm font-medium">
            {t('pageOf', {
              currentPage,
              numberOfPages: numberOfAvailablePages,
            })}
          </div>
        </>
      )}
      <Button
        disabled={currentPage >= numberOfAvailablePages}
        onClick={() => setNewPage(currentPage + 1)}
        buttonType={ButtonType.SECONDARY}
        IconTrailing={IconType.ArrowRight}
        label={<span className="hidden sm:inline">{t('next')}</span>}
        data-testid="next-button"
      />
    </nav>
  );
}

const generateFiller = (
  numberOfAvailablePages: number,
  currentPage: number,
  generatePageButton: (pageNumber: number) => React.JSX.Element,
) => {
  return (
    <Fragment key="filler">
      {currentPage - 1 > 4 && (
        <span
          key="spacer-1"
          className="inline-flex items-center px-4 text-sm font-medium text-gray-500"
          data-testid="first-spacer"
        >
          ...
        </span>
      )}
      {currentPage > 4 &&
        currentPage < numberOfAvailablePages - 1 &&
        generatePageButton(currentPage - 1)}
      {currentPage > 3 &&
        currentPage < numberOfAvailablePages - 2 &&
        generatePageButton(currentPage)}
      {currentPage > 2 &&
        currentPage < numberOfAvailablePages - 3 &&
        generatePageButton(currentPage + 1)}
      {currentPage + 1 < numberOfAvailablePages - 3 && (
        <span
          key="spacer-2"
          className="inline-flex items-center px-4 text-sm font-medium text-gray-500"
          data-testid="second-spacer"
        >
          ...
        </span>
      )}
    </Fragment>
  );
};
